/**
 * @file diff.ts
 * @description «Что изменено» на странице или в месте (phase19.16 Р3а; SB126;
 * db-design-r3a §2 колонка `changes`, §3.1; uiux-r3 §3.1–§3.2).
 *
 * `diffSitePage(before | null, after)` — подробности для шторки «Что
 * изменено», окна публикации (раскрытие «›» по блокам) и истории (версия N
 * против N−1): свойства страницы словами («Адрес: /about → /about-us»),
 * блоки по ключу (= id строки блока) — добавлен / изменён / удалён, как
 * `diffProductTemplate`, и порядок блоков отдельной строкой.
 * `summarizePageDiff(diff)` — сводка для колонки `SitePageRevision.changes`,
 * `formatPageChanges(changes)` — строка для людей по SB126: «блоков изменено
 * 2, добавлено 1, удалено 1».
 *
 * Перестановка блоков — тоже правка (снимок и отпечаток другие, публикация
 * нужна), но не «изменение блока»: считается отдельно (`moved`) как число
 * блоков, которые надо переставить, — длина общего списка минус наибольшая
 * общая подпоследовательность. Так перенос одного блока в конец — 1, а не
 * «сдвинулись все».
 *
 * «SEO-техника» v0.19.65 (SB146): свойство «Хлебные крошки» — «как на сайте /
 * показывать / скрыть»; в нормализованном снимке `INHERIT` без ключа.
 *
 * Названия дизайнов в снимке нет (§3.1) — строка дизайна несёт id в `refs`,
 * имя подставляет кабинет.
 */

import { canonicalJson } from "../site-theme/snapshot.js";
import {
  normalizeSitePageSnapshot,
  readSitePageBreadcrumbs,
  sitePageCanonicalJson,
  type SitePageBreadcrumbs,
  type SitePageSection,
  type SitePageSnapshot,
  type SitePageSnapshotKind,
} from "./snapshot.js";

// ── Типы ──

/** Свойства страницы, которые сравниваются по отдельности (порядок — порядок строк). */
export const SITE_PAGE_DIFF_FIELDS = [
  "title",
  "slug",
  "pageKind",
  "seoTitle",
  "seoDescription",
  "noIndex",
  "breadcrumbs",
  "background",
  "design",
] as const;
export type SitePageDiffField = (typeof SITE_PAGE_DIFF_FIELDS)[number];

/** Подписи свойств — как в «Настройках страницы» кабинета (PageSettingsPanel). */
export const SITE_PAGE_DIFF_FIELD_LABELS: Readonly<Record<SitePageDiffField, string>> = {
  title: "Название",
  slug: "Адрес",
  pageKind: "Роль страницы",
  seoTitle: "Заголовок в поиске",
  seoDescription: "Описание в поиске",
  noIndex: "Не показывать страницу в поиске",
  breadcrumbs: "Хлебные крошки",
  background: "Фон страницы",
  design: "Дизайн страницы",
};

/** Хлебные крошки словами (SB146) — как в «Настройках страницы». */
export const SITE_PAGE_BREADCRUMBS_LABELS: Readonly<Record<SitePageBreadcrumbs, string>> = {
  INHERIT: "как на сайте",
  SHOW: "показывать",
  HIDE: "скрыть",
};

/** Роль страницы словами (меняется только «Сделать главной», SB29). */
export const SITE_PAGE_KIND_LABELS: Readonly<Record<SitePageSnapshotKind, string>> = {
  SYSTEM_HOME: "главная",
  CUSTOM: "обычная страница",
  SLOT: "место на странице",
};

export interface SitePageFieldDiffItem {
  type: "field";
  field: SitePageDiffField;
  label: string;
  /** Значение словами; null — «пусто» (кабинет рисует «—»). */
  before: string | null;
  after: string | null;
  /** Для `design`: id дизайнов до и после (имя подставляет кабинет). */
  refs?: { before: string | null; after: string | null };
}

export type SitePageSectionChange = "added" | "changed" | "removed";

export interface SitePageSectionDiffItem {
  type: "section";
  change: SitePageSectionChange;
  /** Ключ блока (= id строки `SitePageBlock`). */
  key: string;
  /** Шапка раздела, первый заголовок или «Блок N». */
  title: string;
  /** Место блока с 1: в новой версии (добавлен/изменён) или в старой (удалён). */
  position: number;
}

export interface SitePageOrderDiffItem {
  type: "order";
  label: string;
  /** Сколько блоков переставлено (см. шапку файла). */
  moved: number;
}

export type SitePageDiffItem = SitePageFieldDiffItem | SitePageSectionDiffItem | SitePageOrderDiffItem;

export interface SitePageDiffCounts {
  added: number;
  changed: number;
  removed: number;
  moved: number;
  /** Число изменённых свойств страницы. */
  fields: number;
}

export interface SitePageDiff {
  /** Предыдущей версии не было (первая публикация, перенос «Как было на сайте до черновиков»). */
  first: boolean;
  /** Отличаются ли снимки (= отличаются отпечатки); источник правды для «есть правки». */
  hasChanges: boolean;
  items: SitePageDiffItem[];
  counts: SitePageDiffCounts;
}

// ── Подписи значений ──

/** Адрес для людей: главная — «/», остальное — «/<slug>». */
export function sitePageAddress(slug: string): string {
  return slug === "home" ? "/" : `/${slug}`;
}

/** Название блока для строк «что изменено»: шапка раздела, первый заголовок, иначе «Блок N». */
export function sitePageSectionTitle(section: SitePageSection, position: number): string {
  const header = section.payload.header?.title?.trim();
  if (header) return header;
  for (const column of section.payload.columns.items) {
    for (const element of column.elements) {
      if (element.kind === "heading") {
        const text = element.text?.trim();
        if (text) return text;
      }
    }
  }
  return `Блок ${position}`;
}

function fieldItem(
  field: SitePageDiffField,
  before: string | null,
  after: string | null,
  refs?: SitePageFieldDiffItem["refs"],
): SitePageFieldDiffItem {
  return { type: "field", field, label: SITE_PAGE_DIFF_FIELD_LABELS[field], before, after, ...(refs ? { refs } : {}) };
}

/** Фон: «как у сайта» / «свой»; оба свои и разные — «изменён». */
function backgroundItem(before: SitePageSnapshot["background"], after: SitePageSnapshot["background"]): SitePageFieldDiffItem | null {
  if (canonicalJson(before) === canonicalJson(after)) return null;
  const word = (bg: SitePageSnapshot["background"]): string => (bg === null ? "как у сайта" : "свой");
  if (before !== null && after !== null) return fieldItem("background", null, "изменён");
  return fieldItem("background", word(before), word(after));
}

/** Дизайн: «как у сайта» / «свой дизайн»; тот же дизайн с другим содержимым — «обновлён». */
function designItem(before: SitePageSnapshot["design"], after: SitePageSnapshot["design"]): SitePageFieldDiffItem | null {
  if (canonicalJson(before) === canonicalJson(after)) return null;
  const refs = { before: before?.designId ?? null, after: after?.designId ?? null };
  if (before !== null && after !== null) {
    return before.designId === after.designId
      ? fieldItem("design", null, "обновлён", refs)
      : fieldItem("design", "свой дизайн", "другой дизайн", refs);
  }
  const word = (d: SitePageSnapshot["design"]): string => (d === null ? "как у сайта" : "свой дизайн");
  return fieldItem("design", word(before), word(after), refs);
}

function fieldItems(before: SitePageSnapshot, after: SitePageSnapshot): SitePageFieldDiffItem[] {
  const items: SitePageFieldDiffItem[] = [];
  const yesNo = (v: boolean): string => (v ? "да" : "нет");
  for (const field of SITE_PAGE_DIFF_FIELDS) {
    switch (field) {
      case "title":
        if (before.title !== after.title) items.push(fieldItem(field, before.title, after.title));
        break;
      case "slug":
        if (before.slug !== after.slug) items.push(fieldItem(field, sitePageAddress(before.slug), sitePageAddress(after.slug)));
        break;
      case "pageKind":
        if (before.pageKind !== after.pageKind) {
          items.push(fieldItem(field, SITE_PAGE_KIND_LABELS[before.pageKind], SITE_PAGE_KIND_LABELS[after.pageKind]));
        }
        break;
      case "seoTitle":
      case "seoDescription":
        // Пустая строка уже сведена к null (normalizeSitePageSnapshot) — «не задано».
        if (before[field] !== after[field]) items.push(fieldItem(field, before[field], after[field]));
        break;
      case "noIndex":
        if (before.noIndex !== after.noIndex) items.push(fieldItem(field, yesNo(before.noIndex), yesNo(after.noIndex)));
        break;
      case "breadcrumbs": {
        // Нормализованный снимок без ключа = INHERIT.
        const b = readSitePageBreadcrumbs(before.breadcrumbs);
        const a = readSitePageBreadcrumbs(after.breadcrumbs);
        if (b !== a) items.push(fieldItem(field, SITE_PAGE_BREADCRUMBS_LABELS[b], SITE_PAGE_BREADCRUMBS_LABELS[a]));
        break;
      }
      case "background": {
        const item = backgroundItem(before.background, after.background);
        if (item) items.push(item);
        break;
      }
      case "design": {
        const item = designItem(before.design, after.design);
        if (item) items.push(item);
        break;
      }
    }
  }
  return items;
}

/** Длина наибольшей общей подпоследовательности (блоков на странице ≤ 30 — квадрат дёшев). */
function lcsLength(a: readonly string[], b: readonly string[]): number {
  const row = new Array<number>(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i += 1) {
    let prev = 0;
    for (let j = 1; j <= b.length; j += 1) {
      const saved = row[j]!;
      row[j] = a[i - 1] === b[j - 1] ? prev + 1 : Math.max(row[j]!, row[j - 1]!);
      prev = saved;
    }
  }
  return row[b.length]!;
}

/** «N блоков» с согласованием числа. */
export function sitePageBlocksWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} блок`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} блока`;
  return `${n} блоков`;
}

function sectionItems(
  before: readonly SitePageSection[],
  after: readonly SitePageSection[],
): { items: Array<SitePageSectionDiffItem | SitePageOrderDiffItem>; moved: number } {
  const jsonBefore = new Map(before.map((s) => [s.key, canonicalJson(s)]));
  const keysAfter = new Set(after.map((s) => s.key));
  const items: Array<SitePageSectionDiffItem | SitePageOrderDiffItem> = [];
  after.forEach((s, i) => {
    const old = jsonBefore.get(s.key);
    if (old === undefined) {
      items.push({ type: "section", change: "added", key: s.key, title: sitePageSectionTitle(s, i + 1), position: i + 1 });
    } else if (old !== canonicalJson(s)) {
      items.push({ type: "section", change: "changed", key: s.key, title: sitePageSectionTitle(s, i + 1), position: i + 1 });
    }
  });
  before.forEach((s, i) => {
    if (!keysAfter.has(s.key)) {
      items.push({ type: "section", change: "removed", key: s.key, title: sitePageSectionTitle(s, i + 1), position: i + 1 });
    }
  });
  // Порядок — только среди блоков, которые есть в обеих версиях.
  const commonBefore = before.filter((s) => keysAfter.has(s.key)).map((s) => s.key);
  const commonAfter = after.filter((s) => jsonBefore.has(s.key)).map((s) => s.key);
  const moved = commonBefore.length - lcsLength(commonBefore, commonAfter);
  if (moved > 0) items.push({ type: "order", label: "Изменён порядок блоков", moved });
  return { items, moved };
}

// ── Сравнение ──

/**
 * Различия двух снимков: `before` — версия на сайте (или N−1; null — её не
 * было), `after` — черновик (или версия N). Без предыдущей версии свойства
 * не перечисляются, все блоки — «добавлены».
 */
export function diffSitePage(rawBefore: SitePageSnapshot | null, rawAfter: SitePageSnapshot): SitePageDiff {
  // Сравниваем то же, что входит в отпечаток: лишние ключи сняты, "" в SEO = null.
  const before = rawBefore === null ? null : normalizeSitePageSnapshot(rawBefore);
  const after = normalizeSitePageSnapshot(rawAfter);
  const sections = sectionItems(before?.sections ?? [], after.sections);
  const fields = before === null ? [] : fieldItems(before, after);
  const items: SitePageDiffItem[] = [...fields, ...sections.items];
  const count = (change: SitePageSectionChange): number =>
    sections.items.filter((i) => i.type === "section" && i.change === change).length;
  return {
    first: before === null,
    hasChanges: before === null || sitePageCanonicalJson(before) !== sitePageCanonicalJson(after),
    items,
    counts: {
      added: count("added"),
      changed: count("changed"),
      removed: count("removed"),
      moved: sections.moved,
      fields: fields.length,
    },
  };
}

// ── Сводка для версии ──

/** Колонка `SitePageRevision.changes` (§2): сводка отличий от предыдущей версии. */
export interface SitePageChanges {
  /** Первая версия страницы (предыдущей не было). */
  first: boolean;
  sections: { added: number; changed: number; removed: number; moved: number };
  /** Изменённые свойства страницы по порядку `SITE_PAGE_DIFF_FIELDS`. */
  fields: SitePageDiffField[];
}

/** Сводка различий для записи в версию. */
export function summarizePageDiff(diff: SitePageDiff): SitePageChanges {
  const fields = SITE_PAGE_DIFF_FIELDS.filter((field) =>
    diff.items.some((i) => i.type === "field" && i.field === field),
  );
  const { added, changed, removed, moved } = diff.counts;
  return { first: diff.first, sections: { added, changed, removed, moved }, fields };
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const count = (value: unknown): number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : 0;

/**
 * Чтение колонки `changes` (история): умолчание `{}` и битые значения —
 * нули, неизвестные свойства отбрасываются. Никогда не бросает.
 */
export function readSitePageChanges(raw: unknown): SitePageChanges {
  const obj = isPlainObject(raw) ? raw : {};
  const sec = isPlainObject(obj["sections"]) ? obj["sections"] : {};
  const rawFields = Array.isArray(obj["fields"]) ? obj["fields"] : [];
  return {
    first: obj["first"] === true,
    sections: {
      added: count(sec["added"]),
      changed: count(sec["changed"]),
      removed: count(sec["removed"]),
      moved: count(sec["moved"]),
    },
    fields: SITE_PAGE_DIFF_FIELDS.filter((f) => rawFields.includes(f)),
  };
}

/**
 * Сводка словами (SB126): «блоков изменено 2, добавлено 1, удалено 1;
 * порядок блоков; Название, Адрес». Нулевые счётчики не показываются;
 * первая версия — «первая версия: 5 блоков»; ничего — «без изменений».
 */
export function formatPageChanges(changes: SitePageChanges): string {
  const { added, changed, removed, moved } = changes.sections;
  const parts: string[] = [];
  if (changes.first) {
    parts.push(`первая версия: ${sitePageBlocksWord(added)}`);
  } else {
    const blocks = [
      changed > 0 ? `изменено ${changed}` : null,
      added > 0 ? `добавлено ${added}` : null,
      removed > 0 ? `удалено ${removed}` : null,
    ].filter((p): p is string => p !== null);
    if (blocks.length > 0) parts.push(`блоков ${blocks.join(", ")}`);
    if (moved > 0) parts.push("порядок блоков");
    if (changes.fields.length > 0) parts.push(changes.fields.map((f) => SITE_PAGE_DIFF_FIELD_LABELS[f]).join(", "));
  }
  return parts.length > 0 ? parts.join("; ") : "без изменений";
}

/** Число правок для пилюли «● 3 правки» (блоки + перестановка + свойства). */
export function sitePageChangeCount(changes: SitePageChanges): number {
  const { added, changed, removed, moved } = changes.sections;
  return added + changed + removed + (moved > 0 ? 1 : 0) + changes.fields.length;
}
